import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { navigate } from '@gatsbyjs/reach-router';
import { useReactiveVar } from '@apollo/client';
import { Grid } from '@material-ui/core';
import { globalizationPath, useGlobal, PaymentFooter } from '@rcg/ui-react';
import { formatPrice } from '@rcg/pricing-format';
import {
  NAVIGATION_PATHS,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  FLIGHT_TYPE_CODES,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
} from '../../utils/constants';
import { getSessionStorageItem } from '../../operations/sessionStorage/utils';
import ProgressLineItems from '../../components/ProgressLine';
import ItineraryDetails from '../../components/ItineraryDetails';
import Dictionary from '../../utils/dictionary';
import useStyles from './index.style';
import ReviewCard from '../../components/ReviewCard';
import {
  heldReservationVar,
  selectedReturnVar,
  selectedDepartureVar,
} from '../../operations/localState/reactiveVariables';
import ExtendSessionModal from '../../components/ExtendSessionModal';
import { trackGuestInfoView } from '../../utils/analytics/searchTracking';
import useDictionary from '../../hooks/useDictionary';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import AirFooter from '../../components/AirFooter';

const ChangePriceBanner = ({ priceChange }) => {
  const styles = useCommonStyles();
  const classes = useStyles();

  const {
    data: { lang, market },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const descriptionClickHerePart = getDictionaryValue(
    Dictionary.airPaymentChangePriceBannerDescriptionClickHer.key,
  );
  const descriptionSecondPart = getDictionaryValue(
    Dictionary.airPaymentChangePriceBannerDescriptionSecond.key,
  );
  const descriptionFirstPart = getDictionaryValue(
    Dictionary.airPaymentChangePriceBannerDescriptionFirst.key,
  );
  const descriptionForthPart = getDictionaryValue(
    Dictionary.airPaymentChangePriceBannerDescriptionForth.key,
  );
  const descriptionThirdPart = getDictionaryValue(
    Dictionary.airPaymentChangePriceBannerDescriptionThird.key,
  );
  const price = `${formatPrice(
    priceChange?.pricePerPassenger?.value,
    priceChange?.pricePerPassenger?.currency,
    true,
    true,
    true,
  )}`;
  const clickHereUrl = `${urlPath}${NAVIGATION_PATHS.resultsDeparture}?reSearch=true`;

  return (
    <div className={`${styles.notification} ${classes.notification}`}>
      <NotificationBannerComponent
        title={getDictionaryValue(Dictionary.airPaymentChangePriceBannerTitle.key)}
        type="error"
        renderCloseBtn
        isBannerOpen
        useSlot>
        <div className="notification-slot">
          {descriptionFirstPart}
          <span>{` ${descriptionSecondPart} ${price} ${descriptionThirdPart} `}</span>{' '}
          {descriptionForthPart}{' '}
          <a data-testid="click-here-banner" href={clickHereUrl}>
            {descriptionClickHerePart}
          </a>
        </div>
      </NotificationBannerComponent>
    </div>
  );
};
const wait = (time) => {
  setTimeout(() => {
    const headerElem = document.querySelector('header');
    if (headerElem) {
      headerElem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, time);
};

const getSessionVars = () => {
  return {
    isChangeFlights: getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS),
    currentFlightData: getSessionStorageItem('current-data-flight'),
    currentFlightExtradata: getSessionStorageItem('current-extradata-flight'),
  };
};
const getFlightData = (heldReservationGuest) => {
  const { holdFlight } = heldReservationGuest;
  const { priceChange } = holdFlight;
  return { holdFlight, priceChange };
};

const getHeldReservation = (heldReservationGuest) =>
  heldReservationGuest?.holdFlight.inboundFlight ||
  heldReservationGuest?.holdFlight?.outboundFlight;
const GuestInfo = () => {
  const classes = useStyles();
  const {
    data: { lang, market },
  } = useGlobal();
  const getDictionaryValue = useDictionary(Dictionary);

  const [expanded, setExpanded] = useState(false);
  const [expandedAccordionNewFlight, setExpandedAccordionNewFlight] = useState(true);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);

  const heldReservationGuest = useReactiveVar(heldReservationVar);
  const selectedDeparture = useReactiveVar(selectedDepartureVar) || null;
  const selectedReturn = useReactiveVar(selectedReturnVar) || null;
  const isValidHeldReservation = getHeldReservation(heldReservationGuest);
  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);
  const isOneWay = flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY;

  useEffect(() => {
    if (isValidHeldReservation) {
      trackGuestInfoView();
    }
    // eslint-disable-next-line
    document.querySelector('.main-container').scrollTo(0, 0);
  }, [heldReservationGuest]);

  if (!isValidHeldReservation) {
    window.location.href = `${urlPath}${NAVIGATION_PATHS.resultsDeparture}`;
    return '';
  }
  wait(100);
  const getGuestInfo = () => {
    return {
      departureFlight: selectedDeparture,
      returnFlight: selectedReturn,
    };
  };

  const { isChangeFlights, currentFlightData, currentFlightExtradata } = getSessionVars();
  const { holdFlight, priceChange } = getFlightData(heldReservationGuest);

  const continueToCheckout = () => navigate(`${urlPath}${NAVIGATION_PATHS.payment}`);

  const extraDataGuestInfo = getGuestInfo();

  return (
    <>
      <Helmet>
        <title>{getDictionaryValue(Dictionary.airReviewFlightSeoTitle.key)}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <ItineraryDetails />
      <Grid container className={classes.gridContainer}>
        <Grid item xs={12} className={classes.guestInfoContainer}>
          <div className={classes.progressLineContainer}>
            <ProgressLineItems selectedFlightType={flightTypeCodeFromSession} />
          </div>
          <div className={classes.guestInfoCardWrapper}>
            {priceChange && (
              <div className={classes.desktopBannerIconGuestInfo}>
                <ChangePriceBanner priceChange={priceChange} />
              </div>
            )}
            {isChangeFlights && (
              <ReviewCard
                title={getDictionaryValue(Dictionary.airPaymentGuestInfoFlightSummaryTitle.key)}
                data={currentFlightData}
                extraData={currentFlightExtradata}
                isChangeFlights={isChangeFlights}
                isSummary
                handleAccordion={() => setExpanded(!expanded)}
                isDesktop={expanded}
                cardTitle={getDictionaryValue(
                  Dictionary.airCurrentFlightChangeFlightDropdownTitle.key,
                )}
                isOneWay={currentFlightData?.isOneWay}
                isChange
              />
            )}
            <ReviewCard
              title={
                !isChangeFlights &&
                getDictionaryValue(Dictionary.airPaymentGuestInfoFlightSummaryTitle.key)
              }
              data={holdFlight}
              extraData={extraDataGuestInfo}
              isChangeFlights={isChangeFlights}
              isSummary
              handleAccordion={() => setExpandedAccordionNewFlight(!expandedAccordionNewFlight)}
              isDesktop={expandedAccordionNewFlight}
              cardTitle={
                isChangeFlights
                  ? getDictionaryValue(Dictionary.airFlightSummaryNewFlightsLabel.key)
                  : getDictionaryValue(Dictionary.airFlightSummaryTitle.key)
              }
              balanceLabel={isChangeFlights}
              summaryBalance
              isOneWay={isOneWay}
            />
          </div>
          <AirFooter className={classes.airFooter} />
        </Grid>
        <ExtendSessionModal />
        <div className={classes.footerWrapper}>
          <PaymentFooter
            className="sticky-footer"
            paymentSummaryLabel=""
            submitCTA={getDictionaryValue(Dictionary.airGuestInfoStickyFooterCta.key)}
            submitHandler={continueToCheckout}
          />
        </div>
      </Grid>
    </>
  );
};

export default GuestInfo;
